<template>
  <div class="process-panel">
    <div class="toolbar">
      <el-select v-model="searchField" class="toolbar-select">
        <el-option v-for="item in searchFieldOptions" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>
      <el-input
        v-model="searchKeyword"
        clearable
        :placeholder="searchPlaceholder"
        class="toolbar-input"
      />
      <el-select v-model="statusFilter" clearable placeholder="状态" class="toolbar-select">
        <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>
      <el-select
        v-model="categoryFilter"
        multiple
        collapse-tags
        collapse-tags-tooltip
        clearable
        filterable
        placeholder="分类标签"
        class="toolbar-select"
      >
        <el-option
          v-for="item in categoryOptions"
          :key="item.id"
          :label="item.name"
          :value="item.id"
        />
      </el-select>
      <el-button type="primary" :loading="loading" @click="fetchTickets">刷新</el-button>
    </div>

    <div class="process-layout">
      <el-card class="list-card" shadow="never">
        <template #header>
          <div class="card-header">
            <div>
              <div class="card-title">售后工单列表</div>
              <div class="card-subtitle">优先处理保修未判定、处理中、未结清工单</div>
            </div>
            <el-tag type="info" effect="plain">售后处理专区</el-tag>
          </div>
        </template>

        <el-table
          v-loading="loading"
          :data="filteredTickets"
          row-key="id"
          border
          stripe
          height="360"
          highlight-current-row
          @current-change="handleCurrentChange"
          @row-click="handleCurrentChange"
        >
          <el-table-column prop="id" label="ID" width="76" />
          <el-table-column prop="productName" label="产品" min-width="116" />
          <el-table-column prop="customerName" label="客户/门店" min-width="128" />
          <el-table-column label="保修" width="95">
            <template #default="{ row }">
              <el-tag size="small" :type="getWarrantyType(row.warrantyResult)">
                {{ getWarrantyText(row.warrantyResult) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="收费" width="110">
            <template #default="{ row }">
              {{ getChargeModeText(row.chargeMode) }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="110">
            <template #default="{ row }">
              <el-tag :type="getStatusType(row.status)">{{ getStatusText(row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="时效提醒" min-width="220">
            <template #default="{ row }">
              <div class="sla-cell">
                <el-tag size="small" :type="getSlaType(row)">
                  {{ getSlaLabel(row) }}
                </el-tag>
                <div class="sla-subtext">{{ getSlaSubtext(row) }}</div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="90" fixed="right">
            <template #default="{ row }">
              <el-button type="primary" text @click.stop="selectAndLoad(row)">处理</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>

      <el-card class="detail-card" shadow="never">
        <template #header>
          <div class="card-header detail-card-header">
            <div class="card-header-main">
              <div class="card-title">售后分步骤处理台</div>
              <div class="card-subtitle">先在上方选择工单，再在下方按“第 1 次处理、第 2 次处理……”逐步记录</div>
            </div>
            <el-button v-if="detailTarget?.id" text type="primary" @click="refreshDetail">刷新详情</el-button>
          </div>
        </template>

        <template v-if="detailTarget">
          <div class="summary-card">
            <div>
              <div class="summary-title">
                <span>{{ detailTarget.title || detailTarget.productName || `工单 #${detailTarget.id}` }}</span>
                <el-tag :type="getStatusType(detailTarget.status)" effect="dark">{{ getStatusText(detailTarget.status) }}</el-tag>
              </div>
              <div class="summary-meta">
                <span>{{ detailTarget.workOrderNo || `工单 #${detailTarget.id}` }}</span>
                <span>{{ detailTarget.customerName || '—' }}</span>
                <span>{{ detailTarget.contact || '—' }}</span>
                <span>{{ detailTarget.phone || '—' }}</span>
              </div>
            </div>
            <div class="summary-assignee">
              <div class="summary-label">当前处理人</div>
              <div class="summary-value">{{ formatPersonDisplay(detailTarget.processedBy || detailTarget.assignee) }}</div>
            </div>
          </div>

          <el-steps :active="stepProgress" align-center finish-status="success" class="steps-bar">
            <el-step title="受理" />
            <el-step title="分步处理" />
            <el-step title="结算确认" />
            <el-step title="结单" />
          </el-steps>

          <div class="overview-grid">
            <div class="overview-item">
              <span class="overview-label">报单时间</span>
              <span class="overview-value">{{ formatDate(detailTarget.reportTime || detailTarget.createdAt) }}</span>
            </div>
            <div class="overview-item">
              <span class="overview-label">授权码/地址</span>
              <span class="overview-value">{{ detailTarget.storeAuthCode || '—' }}</span>
            </div>
            <div class="overview-item">
              <span class="overview-label">设备</span>
              <span class="overview-value">{{ detailTarget.deviceArchiveName || detailTarget.productName || '—' }}</span>
            </div>
            <div class="overview-item overview-item-wide">
              <span class="overview-label">问题描述</span>
              <span class="overview-value">{{ detailTarget.description || '—' }}</span>
            </div>
          </div>

          <div class="action-grid">
            <div class="action-card">
              <div class="action-title">处理人和状态</div>
              <el-select
                v-model="actionForm.assigneeId"
                filterable
                clearable
                :loading="loadingAfterSalesUsers"
                placeholder="请选择售后处理人"
                style="width: 100%"
              >
                <el-option
                  v-for="item in afterSalesUsers"
                  :key="item.id"
                  :label="formatPersonDisplay(item)"
                  :value="item.id"
                />
              </el-select>
              <div class="action-row">
                <el-button plain @click="assignToMe">指派给我</el-button>
                <el-button type="primary" plain :loading="savingAssignee" @click="saveAssignee">更新处理人</el-button>
              </div>
              <el-radio-group v-model="actionForm.status" class="status-group">
                <el-radio-button v-for="item in statusActionOptions" :key="item.value" :label="item.value">
                  {{ item.label }}
                </el-radio-button>
              </el-radio-group>
              <div class="hint-text" v-if="actionForm.status === 'CLOSED' && missingCloseItems.length">
                当前还缺少 {{ missingCloseItems.length }} 项结单条件
              </div>
              <el-button type="primary" :loading="savingStatus" @click="saveStatus">保存状态</el-button>
            </div>

            <div class="action-card">
              <div class="action-title">保修范围调整</div>
              <div class="meta-line">
                <span>创建工单时已做初判，这里仅在需要时修改</span>
                <span>{{ formatDate(detailTarget.warrantyStartAt) }} 至 {{ formatDate(detailTarget.warrantyEndAt) }}</span>
              </div>
              <el-select
                v-model="actionForm.warrantyResult"
                placeholder="请选择是否在保修范围内"
                style="width: 100%"
                @change="handleWarrantyChange"
              >
                <el-option label="保内" value="IN_WARRANTY" />
                <el-option label="保外" value="OUT_OF_WARRANTY" />
                <el-option label="待确认" value="UNKNOWN" />
              </el-select>
              <el-input
                v-model="actionForm.warrantyRemark"
                type="textarea"
                :rows="3"
                placeholder="保修备注：特殊情况或补充说明写这里"
              />
            </div>

            <div class="action-card action-card-wide">
              <div class="action-title">第 {{ nextStepNumber }} 次处理</div>
              <div class="form-grid">
                <el-select v-model="stepForm.followupType" placeholder="本次处理方式">
                  <el-option label="电话沟通" value="PHONE" />
                  <el-option label="远程处理" value="REMOTE" />
                  <el-option label="上门处理" value="ONSITE" />
                  <el-option label="配件寄送" value="PARTS_SHIPMENT" />
                  <el-option label="客户退货" value="CUSTOMER_RETURN" />
                  <el-option label="其他" value="OTHER" />
                </el-select>
                <el-select v-model="stepForm.partUsage" placeholder="本次是否涉及配件">
                  <el-option label="未使用配件" value="NO_PART" />
                  <el-option label="已使用/准备使用配件" value="USE_PART" />
                  <el-option label="待确认" value="PENDING" />
                </el-select>
              </div>
              <div class="form-grid">
                <div class="readonly-box">
                  <span class="readonly-label">当前保修结果</span>
                  <strong>{{ getWarrantyText(actionForm.warrantyResult) }}</strong>
                  <span class="money-sub">如需修改，请使用上方“保修范围调整”</span>
                </div>
                <el-select v-model="stepForm.hasLaborFee" placeholder="本次是否有人工费">
                  <el-option :value="false" label="无人工费" />
                  <el-option :value="true" label="有人工费" />
                </el-select>
                <el-input-number
                  v-model="stepForm.laborAmount"
                  :precision="2"
                  :min="0"
                  controls-position="right"
                  :disabled="!stepForm.hasLaborFee"
                  class="field-number-input"
                  placeholder="本次人工费"
                />
              </div>
              <div class="form-grid">
                <el-input
                  v-model="stepForm.expressTrackingNo"
                  :placeholder="getExpressTrackingPlaceholder(stepForm.followupType)"
                />
                <div class="upload-box">
                  <el-button plain @click="triggerExpressUpload">{{ getExpressUploadButtonText(stepForm.followupType) }}</el-button>
                  <a v-if="stepForm.expressImageUrl" :href="stepForm.expressImageUrl" target="_blank" rel="noreferrer" class="image-link">
                    查看已上传图片
                  </a>
                  <span v-else class="hint-text">{{ getExpressHintText(stepForm.followupType) }}</span>
                </div>
                <input ref="expressImageInput" type="file" accept="image/*" class="hidden-input" @change="handleExpressImageChange" />
              </div>
              <el-input
                v-model="stepForm.stepResult"
                type="textarea"
                :rows="4"
                placeholder="填写本次处理结果，例如：检查了什么、用了什么配件、客户反馈如何"
              />
              <div class="action-row">
                <span class="hint-text">处理时间按当前保存时间自动记录</span>
                <el-button type="primary" plain :loading="savingStep" @click="saveStepRecord">保存本次处理</el-button>
              </div>
            </div>

            <div class="action-card">
              <div class="action-title">当前步骤配件</div>
              <div class="action-row">
                <div class="meta-stack">
                  <span class="hint-text">先保存本次处理，再把配件挂到对应步骤</span>
                  <span class="hint-text">当前配件金额：{{ formatMoney(detailTarget.partTotalAmount) }}</span>
                </div>
                <el-button type="primary" plain :disabled="!hasSavedSteps" @click="openPartDialog">添加配件</el-button>
              </div>
              <el-table :data="partReplacements" border stripe height="240" empty-text="暂无配件记录">
                <el-table-column label="步骤" width="92">
                  <template #default="{ row }">
                    {{ row.followupStepNo ? `第${row.followupStepNo}次` : '未归属' }}
                  </template>
                </el-table-column>
                <el-table-column prop="partName" label="配件" min-width="120" />
                <el-table-column prop="replacementCount" label="数量" width="72" />
                <el-table-column label="单价" width="96">
                  <template #default="{ row }">{{ formatMoney(row.unitPrice) }}</template>
                </el-table-column>
                <el-table-column label="金额" width="96">
                  <template #default="{ row }">{{ formatMoney(row.totalPrice) }}</template>
                </el-table-column>
                <el-table-column label="操作" width="96" fixed="right">
                  <template #default="{ row }">
                    <el-button type="danger" text @click="removePartReplacement(row)">移除</el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <div class="action-card">
              <div class="action-title">最终处理与结算</div>
              <div class="form-grid">
                <div class="field-box">
                  <span class="field-label">收费方式</span>
                  <el-select
                    v-model="actionForm.chargeMode"
                    :disabled="actionForm.warrantyResult === 'IN_WARRANTY'"
                    placeholder="请选择收费方式"
                    @change="handleChargeModeChange"
                  >
                    <el-option v-if="actionForm.warrantyResult === 'IN_WARRANTY'" label="保修免费" value="WARRANTY_FREE" />
                    <template v-else>
                      <el-option label="正常收费" value="NORMAL_CHARGE" />
                      <el-option label="特殊案例" value="SPECIAL_FREE" />
                    </template>
                  </el-select>
                </div>
                <div class="field-box">
                  <span class="field-label">人工费</span>
                  <el-input-number
                    v-model="actionForm.laborAmount"
                    :precision="2"
                    :min="0"
                    controls-position="right"
                    class="field-number-input"
                    placeholder="人工费（计入成本）"
                    @change="syncReceivableWithBase"
                  />
                </div>
                <div class="readonly-box">
                  <span class="readonly-label">结算方式</span>
                  <strong>默认全额支付</strong>
                  <span class="money-sub">系统自动结算，无需人工核算金额</span>
                </div>
              </div>
              <el-input
                v-model="actionForm.processResult"
                type="textarea"
                :rows="3"
                placeholder="最终处理结果：作为这张工单的总结论"
              />
              <el-input
                v-model="actionForm.chargeRemark"
                type="textarea"
                :rows="3"
                :placeholder="actionForm.chargeMode === 'SPECIAL_FREE' ? '特殊案例必须填写备注原因' : '收费备注：少收原因、审批说明'"
              />
              <el-input
                v-model="actionForm.notes"
                type="textarea"
                :rows="3"
                placeholder="内部备注：其他补充说明"
              />
              <div class="action-row">
                <span class="hint-text">结算默认全额支付，系统自动处理，无需人工核算</span>
                <el-button type="primary" plain :loading="savingReview" @click="saveAfterSalesReview">保存最终处理</el-button>
              </div>
            </div>

            <div class="action-card action-card-wide">
              <div class="action-title">处理日志</div>
              <el-empty v-if="logs.length === 0" description="暂无处理日志" :image-size="72" />
              <el-timeline v-else>
                <el-timeline-item
                  v-for="item in logs"
                  :key="item.id"
                  :timestamp="formatDate(item.createdAt)"
                  placement="top"
                >
                  <div class="timeline-title">{{ getActionText(item.actionType) }}</div>
                  <div class="timeline-content">{{ item.actionDetail || '—' }}</div>
                  <div class="timeline-meta">记录人：{{ item.operatorName || '系统' }}</div>
                </el-timeline-item>
              </el-timeline>
            </div>

            <div class="action-card action-card-wide">
              <div class="action-title">结单校验</div>
              <div v-if="missingCloseItems.length" class="validation-list">
                <div v-for="item in missingCloseItems" :key="item" class="validation-item">
                  {{ item }}
                </div>
              </div>
              <el-empty v-else description="当前已满足结单条件" :image-size="70" />
            </div>
          </div>
        </template>

        <el-empty v-else description="请选择左侧工单开始处理" :image-size="90" />
      </el-card>
    </div>

    <el-dialog v-model="partDialogVisible" title="添加配件" width="520px">
      <div class="part-dialog-body">
        <div class="field-box">
          <span class="field-label">归属处理</span>
          <div class="readonly-box part-belong-tip">
            <strong>默认归属最近一次已保存的处理步骤</strong>
            <span class="money-sub">系统会自动挂到最新处理记录，无需单独选择</span>
          </div>
        </div>
        <div class="field-box">
          <span class="field-label">配件名称</span>
          <el-select v-model="partForm.partId" filterable placeholder="请选择配件" class="part-select">
            <el-option
              v-for="item in partOptions"
              :key="item.id"
              :label="`${item.standardName} (${formatMoney(item.defaultUnitPrice)})`"
              :value="item.id"
            />
          </el-select>
        </div>
        <div class="field-box">
          <span class="field-label">配件数量</span>
          <el-input-number v-model="partForm.replacementCount" :min="1" :precision="0" controls-position="right" class="part-qty-input" />
        </div>
      </div>
      <template #footer>
        <el-button @click="partDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="savingPart" @click="addPartReplacement">保存配件</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { TicketsAPI } from '@/request/tickets'
import { MasterDataAPI } from '@/request/masterData'
import { WorkOrderCategoriesAPI } from '@/request/workOrderCategories'
import { AdminTechniciansAPI } from '@/request/users'
import { useUserStore } from '@/stores/user'
import { lowerText, trimText } from '@/utils/text'
import {
  createWorkOrderStatusOptions,
  createWorkOrderSearchFieldOptions,
  getWorkOrderSearchPlaceholder,
  getWorkOrderSlaMeta,
  getWorkOrderStatusText,
  getWorkOrderStatusType,
  matchesWorkOrderKeyword,
  matchesWorkOrderStatusFilter,
  normalizeWorkOrderStatus
} from '@/utils/workOrderUi'

const createEmptyStepForm = () => ({
  followupType: 'OTHER',
  partUsage: 'PENDING',
  hasLaborFee: false,
  laborAmount: 0,
  expressTrackingNo: '',
  expressImageUrl: '',
  stepResult: ''
})

export default {
  name: 'WorkOrderProcessPanel',
  setup() {
    const userStore = useUserStore()
    const expressImageInput = ref(null)
    const countdownTimer = ref(null)
    const nowTick = ref(Date.now())
    const loading = ref(false)
    const loadingAfterSalesUsers = ref(false)
    const savingStatus = ref(false)
    const savingAssignee = ref(false)
    const savingReview = ref(false)
    const savingStep = ref(false)
    const savingPart = ref(false)
    const partDialogVisible = ref(false)
    const tickets = ref([])
    const logs = ref([])
    const followups = ref([])
    const partReplacements = ref([])
    const partOptions = ref([])
    const detailTarget = ref(null)
    const afterSalesUsers = ref([])
    const searchKeyword = ref('')
    const searchField = ref('WORK_ORDER_NO')
    const searchFieldOptions = createWorkOrderSearchFieldOptions('处理内容')
    const statusOptions = createWorkOrderStatusOptions()
    const statusFilter = ref('')
    const categoryFilter = ref([])
    const categoryOptions = ref([])
    const stepForm = ref(createEmptyStepForm())
    const partForm = ref({
      followupLogId: null,
      partId: null,
      replacementCount: 1
    })
    const actionForm = ref({
      assigneeId: null,
      status: 'PENDING',
      notes: '',
      processResult: '',
      warrantyResult: 'UNKNOWN',
      warrantyRemark: '',
      laborAmount: 0,
      receivableAmount: 0,
      receivedAmount: 0,
      chargeMode: 'NORMAL_CHARGE',
      chargeRemark: ''
    })

    const statusActionOptions = computed(() => {
      return statusOptions
    })

    const hasSavedSteps = computed(() => followups.value.length > 0)
    const nextStepNumber = computed(() => followups.value.length + 1)
    const stepProgress = computed(() => {
      const status = normalizeWorkOrderStatus(detailTarget.value?.status)
      if (status === 'CLOSED') return 3
      if (status === 'PROCESSING') return 1
      return 0
    })

    const searchPlaceholder = computed(() => {
      return getWorkOrderSearchPlaceholder(searchField.value, searchFieldOptions)
    })

    const filteredTickets = computed(() => {
      const keyword = lowerText(searchKeyword.value)
      const selectedCategories = new Set(categoryFilter.value.map((value) => String(value)))
      return tickets.value.filter((item) => {
        if (!matchesWorkOrderStatusFilter(item.status, statusFilter.value)) return false
        if (selectedCategories.size > 0 && !(item.categories || []).some((tag) => selectedCategories.has(String(tag?.id)))) {
          return false
        }
        return matchesWorkOrderKeyword(item, searchField.value, keyword, { includeCategories: true })
      }).sort((left, right) => {
        const rank = (item) => {
          const normalizedStatus = normalizeWorkOrderStatus(item.status)
          if (normalizedStatus === 'CLOSED') return 3
          if (!item.warrantyResult || item.warrantyResult === 'UNKNOWN') return -2
          if (normalizedStatus === 'PROCESSING') return -1
          return 0
        }
        const rankDiff = rank(left) - rank(right)
        if (rankDiff !== 0) return rankDiff
        const leftTime = new Date(left.reportTime || left.createdAt || 0).getTime()
        const rightTime = new Date(right.reportTime || right.createdAt || 0).getTime()
        return rightTime - leftTime
      })
    })

    const currentSettlementStatus = computed(() => {
      if (actionForm.value.chargeMode === 'WARRANTY_FREE' || actionForm.value.chargeMode === 'SPECIAL_FREE') {
        return 'WAIVED'
      }
      const receivable = Number(actionForm.value.receivableAmount || 0)
      const received = Number(actionForm.value.receivedAmount || 0)
      if (receivable <= 0) return 'UNSETTLED'
      if (received < receivable) return received <= 0 ? 'UNSETTLED' : 'PARTIAL'
      return 'PAID'
    })

    const currentPartAmount = computed(() => Number(detailTarget.value?.partTotalAmount || 0))

    const currentOutstandingAmount = computed(() => {
      if (actionForm.value.chargeMode === 'WARRANTY_FREE' || actionForm.value.chargeMode === 'SPECIAL_FREE') {
        return 0
      }
      const receivable = Number(actionForm.value.receivableAmount || 0)
      const received = Number(actionForm.value.receivedAmount || 0)
      return Math.max(0, Number((receivable - received).toFixed(2)))
    })

    const missingCloseItems = computed(() => {
      const items = []
      if (!detailTarget.value) return items
      if (!actionForm.value.warrantyResult || actionForm.value.warrantyResult === 'UNKNOWN') items.push('请先确认是否保内或保外')
      if (!followups.value.length) items.push('请至少记录 1 次处理过程')
      if (!trimText(actionForm.value.processResult)) items.push('请填写最终处理结果')
      if (!actionForm.value.chargeMode) items.push('请确认收费方式')
      if (actionForm.value.chargeMode === 'WARRANTY_FREE' && actionForm.value.warrantyResult !== 'IN_WARRANTY') {
        items.push('保内免费仅适用于保内工单')
      }
      if (actionForm.value.chargeMode === 'SPECIAL_FREE' && !trimText(actionForm.value.chargeRemark)) {
        items.push('特殊案例必须填写备注')
      }
      return items
    })

    const getActionText = (action) => ({
      CREATE: '创建工单',
      ASSIGN: '分配处理人',
      STATUS_CHANGE: '状态变更',
      PROCESS: '处理记录',
      NOTE: '新增备注',
      UPDATE: '更新工单',
      ADMIN_UPDATE: '管理员更新',
      VERIFY: '处理确认',
      FOLLOW_UP: '处理记录',
      CLOSE: '关闭工单'
    }[action] || action || '处理日志')

    const getStatusText = getWorkOrderStatusText
    const getStatusType = getWorkOrderStatusType

    const getWarrantyText = (value) => ({
      IN_WARRANTY: '保内',
      OUT_OF_WARRANTY: '脱保',
      UNKNOWN: '未知'
    }[value] || '未判定')

    const getWarrantyType = (value) => ({
      IN_WARRANTY: 'success',
      OUT_OF_WARRANTY: 'warning',
      UNKNOWN: 'info'
    }[value] || 'info')

    const getChargeModeText = (value) => ({
      WARRANTY_FREE: '保内免费',
      NORMAL_CHARGE: '正常收费',
      SPECIAL_FREE: '特批免费'
    }[value] || '未设置')

    const getSettlementText = (value) => ({
      UNSETTLED: '欠款',
      PARTIAL: '欠款',
      PAID: '已结清',
      WAIVED: '免费结清'
    }[value] || '未结算')

    const getSettlementType = (value) => ({
      UNSETTLED: 'danger',
      PARTIAL: 'danger',
      PAID: 'success',
      WAIVED: 'info'
    }[value] || 'info')

    const getFollowupTypeText = (value) => ({
      PHONE: '电话沟通',
      REMOTE: '远程处理',
      ONSITE: '上门处理',
      PARTS_SHIPMENT: '配件寄送',
      CUSTOMER_RETURN: '客户退货',
      OTHER: '其他'
    }[value] || '其他')

    const isShipmentOrReturnType = (value) => value === 'PARTS_SHIPMENT' || value === 'CUSTOMER_RETURN'

    const getExpressTrackingLabel = (value) => {
      if (value === 'CUSTOMER_RETURN') return '退货单号'
      return '快递单号'
    }

    const getExpressTrackingPlaceholder = (value) => {
      if (value === 'CUSTOMER_RETURN') return '退货单号，可不填；与退货图片二选一即可'
      return '快递单号，可不填；与快递图片二选一即可'
    }

    const getExpressUploadButtonText = (value) => {
      if (value === 'CUSTOMER_RETURN') return '上传退货图片'
      return '上传快递图片'
    }

    const getExpressHintText = (value) => {
      if (value === 'CUSTOMER_RETURN') return '退货单号或退货图片至少填写一项'
      if (value === 'PARTS_SHIPMENT') return '快递单号或快递图片至少填写一项'
      return '有凭证可上传，便于后续核对'
    }

    const loadCategoryOptions = async () => {
      try {
        const list = await WorkOrderCategoriesAPI.list({ enabledOnly: true, scopeType: 'MAINTENANCE' })
        categoryOptions.value = Array.isArray(list) ? list : []
      } catch {
        categoryOptions.value = []
      }
    }

    const formatDate = (value) => {
      if (!value) return '—'
      try {
        return new Intl.DateTimeFormat('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        }).format(new Date(value))
      } catch {
        return String(value)
      }
    }

    const formatMoney = (value) => {
      const amount = Number(value || 0)
      return Number.isFinite(amount) ? `${amount.toFixed(2)} 元` : '0.00 元'
    }

    const formatPersonDisplay = (person, emptyText = '未指派') => {
      if (!person) return emptyText
      const username = trimText(person.username)
      const displayName = trimText(person.displayName)
      if (username && displayName && username !== displayName) {
        return `${username} ${displayName}`
      }
      return username || displayName || emptyText
    }

    const getSlaMeta = (row) => getWorkOrderSlaMeta(row, nowTick.value)

    const getSlaType = (row) => getSlaMeta(row).type
    const getSlaLabel = (row) => getSlaMeta(row).label
    const getSlaSubtext = (row) => getSlaMeta(row).subtext

    const syncReceivableWithBase = () => {
      if (actionForm.value.chargeMode !== 'NORMAL_CHARGE') return
      const partAmount = Number(detailTarget.value?.partTotalAmount || 0)
      const laborAmount = Number(actionForm.value.laborAmount || 0)
      actionForm.value.receivableAmount = Number((partAmount + laborAmount).toFixed(2))
    }

    const handleChargeModeChange = (value) => {
      if (value === 'WARRANTY_FREE' || value === 'SPECIAL_FREE') {
        actionForm.value.receivableAmount = 0
        actionForm.value.receivedAmount = 0
        return
      }
      syncReceivableWithBase()
    }

    const handleWarrantyChange = (value) => {
      if (value === 'IN_WARRANTY') {
        actionForm.value.chargeMode = 'WARRANTY_FREE'
        handleChargeModeChange('WARRANTY_FREE')
      } else if (value === 'OUT_OF_WARRANTY') {
        actionForm.value.chargeMode = 'NORMAL_CHARGE'
        handleChargeModeChange('NORMAL_CHARGE')
      } else {
        actionForm.value.chargeMode = ''
        actionForm.value.receivableAmount = 0
        actionForm.value.receivedAmount = 0
      }
    }

    const buildStepSummary = () => {
      return [
        `第${nextStepNumber.value}次处理`,
        `处理方式：${getFollowupTypeText(stepForm.value.followupType)}`,
        `保修：${getWarrantyText(actionForm.value.warrantyResult)}`,
        `配件情况：${({
          NO_PART: '未使用配件',
          USE_PART: '已使用/准备使用配件',
          PENDING: '待确认'
        }[stepForm.value.partUsage] || '待确认')}`,
        `人工费：${stepForm.value.hasLaborFee ? formatMoney(stepForm.value.laborAmount) : '无'}`,
        `${getExpressTrackingLabel(stepForm.value.followupType)}：${trimText(stepForm.value.expressTrackingNo) || '无'}`,
        `处理结果：${trimText(stepForm.value.stepResult) || '未填写'}`
      ].join('；')
    }

    const createStepFormFromHistory = (followup = null) => ({
      ...createEmptyStepForm(),
      hasLaborFee: Boolean(followup?.hasLaborFee),
      laborAmount: 0
    })

    const syncActionForm = (detail) => {
      actionForm.value = {
        assigneeId: detail?.processedBy?.id || detail?.assignee?.id || null,
        status: normalizeWorkOrderStatus(detail?.status) || 'PENDING',
        notes: detail?.completionRemark || detail?.notes || '',
        processResult: detail?.processResult || detail?.serviceContent || '',
        warrantyResult: detail?.warrantyResult || 'UNKNOWN',
        warrantyRemark: detail?.warrantyRemark || '',
        laborAmount: Number(detail?.laborAmount || 0),
        receivableAmount: Number(detail?.receivableAmount || 0),
        receivedAmount: Number(detail?.receivedAmount || 0),
        chargeMode: detail?.chargeMode || '',
        chargeRemark: detail?.chargeRemark || ''
      }
      if (actionForm.value.warrantyResult === 'IN_WARRANTY' && !actionForm.value.chargeMode) {
        actionForm.value.chargeMode = 'WARRANTY_FREE'
      }
      if (actionForm.value.warrantyResult === 'OUT_OF_WARRANTY' && !actionForm.value.chargeMode) {
        actionForm.value.chargeMode = 'NORMAL_CHARGE'
      }
      if (actionForm.value.chargeMode === 'NORMAL_CHARGE' && (!detail?.receivableAmount || Number(detail.receivableAmount) <= 0)) {
        syncReceivableWithBase()
      }
      if (actionForm.value.chargeMode === 'WARRANTY_FREE' || actionForm.value.chargeMode === 'SPECIAL_FREE') {
        actionForm.value.receivedAmount = 0
      }
    }

    const fetchTickets = async () => {
      try {
        loading.value = true
        const list = await TicketsAPI.getAll({ sourceType: 'AFTER_SALES' })
        tickets.value = Array.isArray(list) ? list : []
      } catch (error) {
        ElMessage.error(error?.message || '获取处理工单失败')
      } finally {
        loading.value = false
      }
    }

    const loadAfterSalesUsers = async () => {
      try {
        loadingAfterSalesUsers.value = true
        const list = await AdminTechniciansAPI.list('', 'ROLE_AFTER_SALES')
        afterSalesUsers.value = Array.isArray(list) ? list : []
      } catch (error) {
        ElMessage.error(error?.message || '获取售后账号失败')
      } finally {
        loadingAfterSalesUsers.value = false
      }
    }

    const loadPartOptions = async () => {
      try {
        const list = await MasterDataAPI.getParts()
        partOptions.value = Array.isArray(list) ? list.filter((item) => item.enabled !== false) : []
      } catch (error) {
        ElMessage.error(error?.message || '获取配件资料失败')
      }
    }

    const loadDetail = async (row) => {
      if (!row?.id) return
      try {
        const [detail, logList, followupList, partList] = await Promise.all([
          TicketsAPI.getById(row.id),
          TicketsAPI.getLogs(row.id),
          TicketsAPI.getFollowups(row.id),
          TicketsAPI.getPartReplacements(row.id)
        ])
        detailTarget.value = detail || row
        logs.value = Array.isArray(logList) ? logList : []
        followups.value = Array.isArray(followupList) ? followupList : []
        partReplacements.value = Array.isArray(partList) ? partList : []
        syncActionForm(detailTarget.value)
        stepForm.value = createStepFormFromHistory(followups.value[followups.value.length - 1])
        partForm.value.followupLogId = followups.value[followups.value.length - 1]?.id || null
      } catch (error) {
        logs.value = []
        ElMessage.error(error?.message || '获取工单详情失败')
      }
    }

    const handleCurrentChange = (row) => {
      if (!row?.id) return
      loadDetail(row)
    }

    const selectAndLoad = (row) => {
      handleCurrentChange(row)
    }

    const refreshDetail = async () => {
      if (!detailTarget.value?.id) return
      await loadDetail(detailTarget.value)
    }

    const saveStatus = async () => {
      if (!detailTarget.value?.id) return
      if (actionForm.value.status === normalizeWorkOrderStatus(detailTarget.value.status)) {
        ElMessage.info('状态未变更')
        return
      }
      if (actionForm.value.status === 'CLOSED' && missingCloseItems.value.length) {
        ElMessage.warning(missingCloseItems.value[0])
        return
      }
      try {
        savingStatus.value = true
        await TicketsAPI.updateStatus(detailTarget.value.id, actionForm.value.status)
        ElMessage.success('工单状态已更新')
        await refreshDetail()
        await fetchTickets()
      } catch (error) {
        ElMessage.error(error?.message || '更新工单状态失败')
      } finally {
        savingStatus.value = false
      }
    }

    const saveAssignee = async () => {
      if (!detailTarget.value?.id) return
      if (!actionForm.value.assigneeId) {
        ElMessage.warning('请选择售后处理人')
        return
      }
      const currentAssigneeId = detailTarget.value?.processedBy?.id || detailTarget.value?.assignee?.id
      if (actionForm.value.assigneeId === currentAssigneeId) {
        ElMessage.info('处理人未变更')
        return
      }
      try {
        savingAssignee.value = true
        await TicketsAPI.assign(detailTarget.value.id, actionForm.value.assigneeId)
        ElMessage.success('处理人已更新')
        await refreshDetail()
        await fetchTickets()
      } catch (error) {
        ElMessage.error(error?.message || '更新处理人失败')
      } finally {
        savingAssignee.value = false
      }
    }

    const assignToMe = () => {
      if (!userStore.userInfo.id) {
        ElMessage.warning('当前账号缺少用户信息')
        return
      }
      actionForm.value.assigneeId = userStore.userInfo.id
    }

    const triggerExpressUpload = () => {
      expressImageInput.value?.click()
    }

    const handleExpressImageChange = async (event) => {
      const file = event?.target?.files?.[0]
      if (!file) return
      try {
        const result = await TicketsAPI.uploadFollowupImage(file)
        stepForm.value.expressImageUrl = result?.url || result?.relativeUrl || ''
        ElMessage.success('凭证图片上传成功')
      } catch (error) {
        ElMessage.error(error?.message || '凭证图片上传失败')
      } finally {
        if (event?.target) {
          event.target.value = ''
        }
      }
    }

    const saveStepRecord = async () => {
      if (!detailTarget.value?.id) return
      if (!stepForm.value.stepResult.trim()) {
        ElMessage.warning('请填写本次处理结果')
        return
      }
      const expressTrackingNo = stepForm.value.expressTrackingNo?.trim() || ''
      const expressImageUrl = stepForm.value.expressImageUrl || ''
      if (isShipmentOrReturnType(stepForm.value.followupType) && !expressTrackingNo && !expressImageUrl) {
        ElMessage.warning(stepForm.value.followupType === 'CUSTOMER_RETURN' ? '客户退货时请填写退货单号或上传退货图片，二选一即可' : '配件寄送时请填写快递单号或上传快递图片，二选一即可')
        return
      }
      try {
        savingStep.value = true
        const created = await TicketsAPI.addFollowup(detailTarget.value.id, {
          followupType: stepForm.value.followupType,
          followupResult: buildStepSummary(),
          warrantyResult: actionForm.value.warrantyResult,
          hasLaborFee: stepForm.value.hasLaborFee,
          laborAmount: Number(stepForm.value.laborAmount || 0),
          expressTrackingNo: expressTrackingNo || null,
          expressImageUrl: expressImageUrl || null
        })
        ElMessage.success(`第 ${nextStepNumber.value} 次处理已保存`)
        partForm.value.followupLogId = created?.id || partForm.value.followupLogId
        const latestSnapshot = created || {
          warrantyResult: actionForm.value.warrantyResult,
          hasLaborFee: stepForm.value.hasLaborFee
        }
        stepForm.value = createStepFormFromHistory(latestSnapshot)
        if (actionForm.value.status === 'PENDING') {
          actionForm.value.status = 'PROCESSING'
          await TicketsAPI.updateStatus(detailTarget.value.id, 'PROCESSING')
        }
        await refreshDetail()
        await fetchTickets()
      } catch (error) {
        ElMessage.error(error?.message || '保存处理记录失败')
      } finally {
        savingStep.value = false
      }
    }

    const addPartReplacement = async () => {
      if (!detailTarget.value?.id) return
      if (!partForm.value.followupLogId) {
        ElMessage.warning('请先保存至少一次处理记录')
        return
      }
      if (!partForm.value.partId) {
        ElMessage.warning('请选择配件')
        return
      }
      try {
        savingPart.value = true
        await TicketsAPI.addPartReplacement(detailTarget.value.id, {
          followupLogId: partForm.value.followupLogId,
          partId: partForm.value.partId,
          replacementCount: Number(partForm.value.replacementCount || 1)
        })
        ElMessage.success('配件记录已保存')
        partDialogVisible.value = false
        partForm.value = { followupLogId: partForm.value.followupLogId, partId: null, replacementCount: 1 }
        await refreshDetail()
        await fetchTickets()
      } catch (error) {
        ElMessage.error(error?.message || '保存配件失败')
      } finally {
        savingPart.value = false
      }
    }

    const removePartReplacement = async (row) => {
      if (!detailTarget.value?.id || !row?.id) return
      try {
        await ElMessageBox.confirm(
          `确认移除配件“${row.partName || '未命名配件'}”吗？移除后会重新计算配件金额。`,
          '移除配件',
          {
            confirmButtonText: '确认移除',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        savingPart.value = true
        await TicketsAPI.removePartReplacement(detailTarget.value.id, row.id)
        ElMessage.success('配件已移除')
        await refreshDetail()
        await fetchTickets()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          ElMessage.error(error?.message || '移除配件失败')
        }
      } finally {
        savingPart.value = false
      }
    }

    const openPartDialog = () => {
      if (!followups.value.length) {
        ElMessage.warning('请先保存至少一次处理记录，再添加配件')
        return
      }
      partForm.value = {
        followupLogId: partForm.value.followupLogId || followups.value[followups.value.length - 1]?.id || null,
        partId: null,
        replacementCount: 1
      }
      partDialogVisible.value = true
    }

    const saveAfterSalesReview = async () => {
      if (!detailTarget.value?.id) return
      try {
        savingReview.value = true
        await TicketsAPI.adminUpdate(detailTarget.value.id, {
          processResult: actionForm.value.processResult?.trim() || null,
          completionRemark: actionForm.value.notes?.trim() || null,
          warrantyResult: actionForm.value.warrantyResult || null,
          warrantyRemark: actionForm.value.warrantyRemark?.trim() || null,
          laborAmount: Number(actionForm.value.laborAmount || 0),
          receivableAmount: Number(actionForm.value.receivableAmount || 0),
          receivedAmount: Number(actionForm.value.receivedAmount || 0),
          chargeMode: actionForm.value.chargeMode || null,
          chargeRemark: actionForm.value.chargeRemark?.trim() || null
        })
        ElMessage.success('最终处理与结算已保存')
        await refreshDetail()
        await fetchTickets()
      } catch (error) {
        ElMessage.error(error?.message || '保存最终处理失败')
      } finally {
        savingReview.value = false
      }
    }

    onMounted(async () => {
      countdownTimer.value = window.setInterval(() => {
        nowTick.value = Date.now()
      }, 60000)
      await Promise.all([fetchTickets(), loadAfterSalesUsers(), loadPartOptions(), loadCategoryOptions()])
    })

    onBeforeUnmount(() => {
      if (countdownTimer.value) {
        window.clearInterval(countdownTimer.value)
        countdownTimer.value = null
      }
    })

    return {
      loading,
      loadingAfterSalesUsers,
      savingStatus,
      savingAssignee,
      savingReview,
      savingStep,
      savingPart,
      filteredTickets,
      detailTarget,
      logs,
      followups,
      partReplacements,
      partOptions,
      expressImageInput,
      partDialogVisible,
      afterSalesUsers,
      searchKeyword,
      searchField,
      searchFieldOptions,
      statusOptions,
      searchPlaceholder,
      statusFilter,
      categoryFilter,
      categoryOptions,
      actionForm,
      stepForm,
      partForm,
      statusActionOptions,
      currentSettlementStatus,
      currentPartAmount,
      currentOutstandingAmount,
      missingCloseItems,
      hasSavedSteps,
      nextStepNumber,
      stepProgress,
      fetchTickets,
      handleCurrentChange,
      selectAndLoad,
      refreshDetail,
      saveStatus,
      saveAssignee,
      assignToMe,
      triggerExpressUpload,
      handleExpressImageChange,
      saveStepRecord,
      addPartReplacement,
      removePartReplacement,
      openPartDialog,
      saveAfterSalesReview,
      getActionText,
      getStatusText,
      getStatusType,
      getWarrantyText,
      getWarrantyType,
      getChargeModeText,
      getSettlementText,
      getSettlementType,
      getFollowupTypeText,
      getExpressTrackingPlaceholder,
      getExpressUploadButtonText,
      getExpressHintText,
      getSlaType,
      getSlaLabel,
      getSlaSubtext,
      formatDate,
      formatMoney,
      formatPersonDisplay,
      handleChargeModeChange,
      handleWarrantyChange,
      syncReceivableWithBase
    }
  }
}
</script>

<style scoped>
.process-panel {
  display: grid;
  gap: 16px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.toolbar-input {
  width: 320px;
}

.toolbar-select {
  width: 180px;
}

.process-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  align-items: start;
}

.list-card,
.detail-card {
  border-radius: 16px;
  border: 1px solid #e5e7eb;
}

.card-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.card-header-main {
  flex: 1;
}

.detail-card-header .card-header-main {
  text-align: center;
}

.card-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}

.card-subtitle {
  margin-top: 4px;
  color: #64748b;
  font-size: 13px;
}

.summary-card {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  margin-bottom: 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%);
  border: 1px solid #dbeafe;
}

.summary-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
}

.summary-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: #64748b;
  font-size: 13px;
}

.summary-assignee {
  min-width: 160px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #dbeafe;
}

.summary-label {
  color: #94a3b8;
  font-size: 12px;
}

.summary-value {
  margin-top: 8px;
  color: #1e293b;
  font-size: 16px;
  font-weight: 700;
}

.steps-bar {
  margin-bottom: 16px;
  padding: 16px 12px;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: #fff;
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.overview-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
}

.overview-item-wide {
  grid-column: 1 / -1;
}

.overview-label,
.readonly-label {
  color: #64748b;
  font-size: 12px;
}

.overview-value {
  color: #0f172a;
  font-size: 14px;
  line-height: 1.5;
}

.action-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.action-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.action-card-wide {
  grid-column: 1 / -1;
}

.action-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.action-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.status-group {
  display: flex;
  flex-wrap: wrap;
}

.hint-text,
.money-sub,
.meta-line,
.timeline-meta {
  color: #64748b;
  font-size: 12px;
}

.meta-line {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.field-box {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  color: #64748b;
  font-size: 12px;
}

.field-box :deep(.el-input),
.field-box :deep(.el-select) {
  width: 100%;
}

.field-number-input {
  width: 100%;
}

.readonly-box {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-height: 72px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px dashed #cbd5e1;
  background: #fff;
}

.sla-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sla-subtext {
  color: #64748b;
  font-size: 12px;
  line-height: 1.4;
}

.part-select {
  width: 100%;
}

.part-dialog-body {
  display: grid;
  gap: 14px;
}

.part-qty-input {
  width: 100%;
}

.meta-stack {
  display: grid;
  gap: 4px;
}

.upload-box {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  flex-wrap: wrap;
}

.hidden-input {
  display: none;
}

.image-link {
  color: #2563eb;
  text-decoration: none;
  font-size: 13px;
}

.image-link:hover {
  text-decoration: underline;
}

.timeline-title {
  font-weight: 700;
  color: #0f172a;
}

.timeline-content {
  margin-top: 6px;
  white-space: pre-wrap;
  line-height: 1.7;
  color: #334155;
}

.timeline-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.timeline-parts {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.part-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #334155;
  font-size: 12px;
}

.validation-list {
  display: grid;
  gap: 10px;
}

.validation-item {
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff7ed;
  border: 1px solid #fdba74;
  color: #9a3412;
  font-size: 13px;
}

@media (max-width: 900px) {
  .action-grid,
  .overview-grid,
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
